"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BannerItem = void 0;
const react_1 = __importDefault(require("react"));
const react_query_1 = require("react-query");
const react_redux_1 = require("react-redux");
const common_1 = require("../../../common");
const banner_1 = require("../../../server/banner");
const classes_1 = require("../../../server/classes");
const account_1 = require("../../base/account");
const DeletePopUpLayout_1 = require("../../base/layout/DeletePopUpLayout");
const EventBandBanner_1 = require("../../base/layout/EventBandBanner");
const popup_1 = require("../../base/popup");
const action_1 = require("../../menu/action");
const ui_1 = require("../ui");
const DropDownInput_1 = require("../ui/DropDownInput");
const BannerPreview_1 = require("./BannerPreview");
const types_1 = require("./types");
/** 대상 드롭다운에서 '전체' 를 나타내는 옵션 id(빈 문자열은 select value 로 불안정) */
const GLOBAL_OPTION_ID = '__global__';
const GLOBAL_TARGET_LABEL = '전체 (개별 설정이 없는 모든 클래스)';
/** 전체 배너 유도 시 조회해 볼 최대 클래스 수(모두 개별 설정이면 판정 불가) */
const GLOBAL_PROBE_LIMIT = 5;
const TITLE_MAX = 60;
const DESC_MAX = 100;
const CTA_MAX = 20;
/**
 * 전체 배너 현재 값을 유도한다. 서버 GET 은 빈 class_id 를 404 로 거절하므로
 * 같은 테넌트의 실제 클래스를 조회해 "전체 배너 적용"(result.class_id === '') 응답을 찾는다.
 *  - null 응답 = 전체 배너 없음(개별도 없는 클래스가 null 이면 전체도 없다)
 *  - 개별 설정 응답 = 다음 클래스로 넘어감(최대 GLOBAL_PROBE_LIMIT)
 */
const probeGlobalBanner = async (candidates) => {
    let probed = 0;
    for (const klass of candidates) {
        if (probed >= GLOBAL_PROBE_LIMIT)
            break;
        probed += 1;
        try {
            const response = await (0, banner_1.getBanner)(klass.id);
            const item = response?.result ?? null;
            if (!item)
                return { status: 'none', item: null };
            if ((item.class_id ?? '') === banner_1.GLOBAL_BANNER_CLASS_ID) {
                return { status: 'own', item: { ...item, class_id: banner_1.GLOBAL_BANNER_CLASS_ID } };
            }
            // 개별 설정 클래스 — 전체 배너를 알 수 없으니 다음 후보
        }
        catch (err) {
            console.warn('[BannerItem] 전체 배너 유도 조회 실패', klass.id, err);
        }
    }
    return { status: 'unknown', item: null };
};
/**
 * 배너 등록/수정 화면.
 *  - 대상: 전체(class_id '') 또는 클래스 개별(uuid). 목록에서 진입하면 대상 고정, 상단 '배너 등록'이면 선택.
 *  - 저장은 설정 API 한 번(upsert) — 등록과 수정이 같은 요청이다.
 *  - 전체 배너는 테넌트(파트너) 단위로 저장된다. default 관리자는 모든 파트너를 관리하므로 어느 파트너의 전체 배너인지
 *    드롭다운으로 고른다(기본 = 자기 테넌트 default). 그 외 관리자는 자기 테넌트가 자동 적용.
 *  - 전체 배너 현재 값: 목록이 넘긴 initialBanner 를 쓰고, 없으면 같은 테넌트 클래스 조회로 유도한다.
 *  - 클래스 대상인데 개별 설정이 없으면 현재 적용 중인 전체 배너 값을 시작점으로 채운다(저장 시 개별 배너가 됨).
 */
exports.BannerItem = react_1.default.memo(({ class_id, class_title, tenant_id, isNew = true, initial: initialProp, initialBanner: initialBannerProp, }) => {
    // initial(구형 별칭)과 initialBanner 둘 다 받는다 — 목록이 어느 쪽으로 넘겨도 동작
    const initialBanner = initialBannerProp !== undefined ? initialBannerProp : initialProp;
    const dispatch = (0, react_redux_1.useDispatch)();
    const userTenantId = (0, react_redux_1.useSelector)((state) => (0, account_1.getUserTenantId)(state));
    const isDefaultAdmin = userTenantId === 'default';
    const isTargetFixed = class_id !== undefined;
    const [targetClassId, setTargetClassId] = react_1.default.useState(class_id ?? banner_1.GLOBAL_BANNER_CLASS_ID);
    // undefined = 아직 조회 전(유도 대기), [] = 조회 완료(없음)
    const [klassList, setKlassList] = react_1.default.useState(undefined);
    // initial = 입력창 초기값(로드 시점에만 바뀜), form = 현재 입력값(미리보기·저장용)
    const [initial, setInitial] = react_1.default.useState(types_1.EMPTY_BANNER);
    const [form, setForm] = react_1.default.useState(types_1.EMPTY_BANNER);
    const [loadedStatus, setLoadedStatus] = react_1.default.useState('loading');
    // 대상에 "현재 적용 중인" 배너(전체 = 그 파트너의 전체 배너, 클래스 = 개별 또는 상속된 전체 배너). 참고 영역·덮어쓰기 확인용
    const [current, setCurrent] = react_1.default.useState(null);
    // 입력창 강제 리셋용 — "불러와서 수정" 시 TextInput 을 remount 해 값이 확실히 바뀌게 한다
    const [formVersion, setFormVersion] = react_1.default.useState(0);
    // 파트너 ID(전체 배너의 적용 테넌트). 목록 카드에서 넘긴 값이 우선, 없으면 관리자 자신의 테넌트(default).
    // ★ 예전엔 후보에서 'default' 를 빼고 후보가 하나면 자동 입력해 mimac 으로 잘못 채워졌다 — 그 결과 전체 배너가
    //   mimac 클래스 12개에만 적용되고 default 63개는 안 바뀌는 "일부만 반영" 증상이 났다.
    const [partnerId, setPartnerId] = react_1.default.useState(tenant_id ?? userTenantId ?? '');
    const postMutation = (0, react_query_1.useMutation)(banner_1.postBanner);
    const deleteMutation = (0, react_query_1.useMutation)(banner_1.deleteBanner);
    const isGlobal = targetClassId === banner_1.GLOBAL_BANNER_CLASS_ID;
    // 대상 선택·파트너 ID 후보·전체 배너 유도용 클래스 목록
    react_1.default.useEffect(() => {
        let cancelled = false;
        (0, classes_1.getClassesList)({ limit: 500, orderby: 'creation_time DESC' })
            .then((response) => {
            if (!cancelled)
                setKlassList(response?.result?.items ?? []);
        })
            .catch(err => {
            // 목록 실패 시 대상 선택·전체 배너 유도가 제한된다(고정 클래스 편집은 계속 가능)
            console.warn('[BannerItem] 클래스 목록 조회 실패', err);
            if (!cancelled)
                setKlassList([]);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    // 테넌트별 클래스 수 — 파트너 드롭다운 표시·적용 범위 문구용. 관리자 자신의 테넌트와 현재 선택값은 항상 포함
    const tenantCounts = react_1.default.useMemo(() => {
        const counts = new Map();
        (klassList ?? []).forEach(klass => {
            if (!klass.tenant_id)
                return;
            counts.set(klass.tenant_id, (counts.get(klass.tenant_id) ?? 0) + 1);
        });
        if (userTenantId && !counts.has(userTenantId))
            counts.set(userTenantId, 0);
        if (partnerId && !counts.has(partnerId))
            counts.set(partnerId, 0);
        // 관리자 자신의 테넌트 → 클래스 많은 순
        return Array.from(counts.entries())
            .map(([id, count]) => ({ id, count }))
            .sort((a, b) => a.id === userTenantId ? -1 : b.id === userTenantId ? 1 : b.count - a.count);
    }, [klassList, userTenantId, partnerId]);
    const tenantOptions = react_1.default.useMemo(() => tenantCounts.map(({ id, count }) => ({
        id,
        value: `${id}${id === userTenantId ? ' (내 테넌트)' : ''} — 클래스 ${count}개`,
    })), [tenantCounts, userTenantId]);
    // 전체 배너의 실제 적용 테넌트: default 관리자는 선택한 파트너, 그 외는 자기 테넌트
    const effectiveTenantId = isDefaultAdmin ? partnerId.trim() : userTenantId;
    const effectiveTenantCount = tenantCounts.find(t => t.id === effectiveTenantId)?.count ?? 0;
    // 전체 배너 유도에 쓸 테넌트(클래스가 있는 테넌트일 때만 필터, 없으면 전체 목록에서 시도)
    const probeTenantId = effectiveTenantId && effectiveTenantCount > 0 ? effectiveTenantId : undefined;
    /**
     * 이미 로드를 마친 대상(전체=파트너 단위 / 클래스=클래스 단위)을 기억한다.
     * ★수정이 되지 않던 원인: 이 effect 의 의존성에 klassList·probeTenantId 가 들어 있어
     *   클래스 목록 응답이 늦게 도착하거나 파트너가 확정될 때마다 다시 실행됐고,
     *   그때마다 apply() 가 폼을 서버 값으로 되돌려 관리자가 입력하던 내용이 사라졌다.
     *   같은 대상은 한 번만 적용하고, 대상이 바뀔 때만 다시 로드한다.
     */
    const appliedKeyRef = react_1.default.useRef(null);
    const loadKey = isGlobal ? `global|${effectiveTenantId ?? ''}` : `class|${targetClassId}`;
    // 대상의 현재 설정 로드
    react_1.default.useEffect(() => {
        if (appliedKeyRef.current === loadKey)
            return;
        let cancelled = false;
        const apply = (result) => {
            if (cancelled)
                return;
            appliedKeyRef.current = loadKey;
            const item = result.item
                ? { ...result.item, class_id: targetClassId }
                : null;
            setLoadedStatus(result.status);
            setCurrent(item);
            if (!isNew) {
                // 수정 진입: 현재 값을 폼에 채운다
                const next = item ?? { ...types_1.EMPTY_BANNER, class_id: targetClassId };
                setInitial(next);
                setForm(next);
                setFormVersion(prev => prev + 1);
            }
            else {
                // 등록 진입: 빈 폼 유지. 현재 값은 상단 "현재 적용 중인 배너" 참고 영역에 보여주고 버튼으로 불러온다
                setForm(prev => ({ ...prev, class_id: targetClassId }));
            }
        };
        setLoadedStatus('loading');
        if (!isGlobal) {
            (0, banner_1.getBanner)(targetClassId)
                .then(response => {
                const item = response?.result ?? null;
                apply({ status: (0, types_1.resolveBannerStatus)(targetClassId, item), item });
            })
                .catch(err => {
                console.warn('[BannerItem] 배너 조회 실패', err);
                apply({ status: 'error', item: null });
            });
            return () => {
                cancelled = true;
            };
        }
        // 전체 배너: 목록이 이미 판정한 값이 있으면 그대로(없음 확인 = null 포함)
        if (initialBanner !== undefined) {
            apply(initialBanner
                ? { status: 'own', item: initialBanner }
                : { status: 'none', item: null });
            return () => {
                cancelled = true;
            };
        }
        // 클래스 목록 도착 전이면 대기(목록이 오면 effect 재실행)
        if (klassList === undefined) {
            return () => {
                cancelled = true;
            };
        }
        const candidates = probeTenantId
            ? klassList.filter(klass => klass.tenant_id === probeTenantId)
            : klassList;
        probeGlobalBanner(candidates).then(apply);
        return () => {
            cancelled = true;
        };
    }, [loadKey, targetClassId, isGlobal, initialBanner, klassList, probeTenantId, isNew]);
    // "이 내용 불러와서 수정" — 현재 적용 중인 배너를 폼에 복사
    const loadCurrentIntoForm = () => {
        if (!current)
            return;
        const next = { ...current, class_id: targetClassId };
        setInitial(next);
        setForm(next);
        setFormVersion(prev => prev + 1);
    };
    const selectedKlass = react_1.default.useMemo(() => (klassList ?? []).find(klass => klass.id === targetClassId), [klassList, targetClassId]);
    const targetLabel = isGlobal
        ? GLOBAL_TARGET_LABEL
        : selectedKlass
            ? `${selectedKlass.title}${selectedKlass.identifier ? ` (${selectedKlass.identifier})` : ''}`
            : class_title ?? targetClassId;
    const targetOptions = react_1.default.useMemo(() => [
        { id: GLOBAL_OPTION_ID, value: GLOBAL_TARGET_LABEL },
        ...(klassList ?? []).map(klass => ({
            id: klass.id,
            value: `${klass.title}${klass.identifier ? ` (${klass.identifier})` : ''}`,
        })),
    ], [klassList]);
    const updateField = (key) => (value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };
    // 적용 범위 문구 — "전체" 가 실제로는 "이 파트너의 전체" 임을 분명히 한다
    const scopeText = isGlobal
        ? effectiveTenantId
            ? `적용 범위: 파트너 ${effectiveTenantId}의 클래스 ${effectiveTenantCount}개 (개별 설정이 있는 클래스 제외)`
            : '적용 범위: 파트너를 선택해야 합니다'
        : `적용 범위: 이 클래스에만 적용${selectedKlass?.tenant_id ? ` (파트너 ${selectedKlass.tenant_id})` : ''}`;
    const statusHint = react_1.default.useMemo(() => {
        switch (loadedStatus) {
            case 'loading':
                return '기존 설정을 확인하는 중...';
            case 'own':
                return isGlobal
                    ? isNew
                        ? '이 파트너에는 전체 배너가 이미 있습니다. 저장하면 그 배너를 덮어씁니다(위 "현재 적용 중인 배너" 참고).'
                        : '현재 전체 배너를 수정하고 있습니다. 저장하면 바로 교체됩니다.'
                    : isNew
                        ? '이 클래스에는 개별 배너가 이미 있습니다. 저장하면 덮어씁니다.'
                        : '이 클래스의 개별 배너를 수정하고 있습니다. 저장하면 바로 교체됩니다.';
            case 'inherit':
                return '⚠ 이 클래스는 현재 전체 배너를 사용 중입니다. 저장하면 이 클래스 전용(개별) 배너가 되어 이후 전체 배너를 바꿔도 따라가지 않습니다. 전체 배너만 바꾸려면 목록의 전체 배너 카드에서 수정하세요.';
            case 'unknown':
                return '전체 배너의 현재 값을 확인할 수 없습니다(조회한 클래스가 모두 개별 설정이거나 해당 파트너의 클래스가 없음). 저장하면 입력한 값으로 설정됩니다.';
            case 'error':
                return '기존 설정을 불러오지 못했습니다. 저장하면 입력한 값으로 새로 설정됩니다.';
            default:
                return isGlobal
                    ? '설정된 배너가 없습니다. 등록하면 개별 설정이 없는 모든 클래스에 노출됩니다.'
                    : '이 클래스에 노출되는 배너가 없습니다.';
        }
    }, [loadedStatus, isGlobal, isNew]);
    const cancelMenu = () => {
        dispatch((0, action_1.goToPrevMenu)());
    };
    const resolveHeaderTenant = () => {
        if (!isGlobal)
            return undefined;
        if (!effectiveTenantId) {
            alert('전체 배너는 적용할 파트너를 선택해야 합니다.');
            return false;
        }
        return effectiveTenantId;
    };
    const handlerSubmit = async () => {
        const title = form.title.trim();
        const link = form.button_link.trim();
        const safeLink = (0, EventBandBanner_1.toSafeHref)(link);
        if (!title) {
            alert('배너 제목을 입력해주세요.');
            return;
        }
        if (link && !safeLink) {
            alert('버튼 링크는 http:// 또는 https:// 로 시작하는 주소여야 합니다.');
            return;
        }
        // 전체 배너 적용 중인 클래스를 개별 배너로 바꾸는 저장은 한 번 더 확인한다
        if (!isGlobal && loadedStatus === 'inherit') {
            const ok = window.confirm('이 클래스 전용(개별) 배너로 저장됩니다.\n이후 전체 배너를 바꿔도 이 클래스는 따라가지 않습니다. 계속할까요?');
            if (!ok)
                return;
        }
        // 등록 진입에서 이미 배너가 있는 대상을 저장하면 덮어쓰기 — 한 번 더 확인
        if (isNew && loadedStatus === 'own' && current) {
            const ok = window.confirm(`현재 적용 중인 ${isGlobal ? '전체' : '개별'} 배너 '${current.title}'를 덮어씁니다. 계속할까요?`);
            if (!ok)
                return;
        }
        const headerTenantId = resolveHeaderTenant();
        if (headerTenantId === false)
            return;
        APP.eventManager.publish('SHOW_LOADING', true);
        try {
            await postMutation.mutateAsync({
                tenant_id: headerTenantId,
                props: {
                    class_id: targetClassId,
                    title,
                    description: form.description.trim(),
                    button_text: form.button_text.trim(),
                    button_link: safeLink ?? '',
                },
            });
            APP.eventManager.publish(types_1.UPDATE_BANNER_LIST);
            APP.eventManager.publish('CHANGE_MENU', { showDepth: 'list', props: undefined });
        }
        catch (error) {
            alert(error);
        }
        finally {
            APP.eventManager.publish('SHOW_LOADING', false);
        }
    };
    const handlerDelete = () => {
        const onDelete = async () => {
            const headerTenantId = resolveHeaderTenant();
            if (headerTenantId === false)
                return;
            APP.eventManager.publish('SHOW_LOADING', true);
            try {
                await deleteMutation.mutateAsync({ class_id: targetClassId, tenant_id: headerTenantId });
                APP.eventManager.publish(types_1.UPDATE_BANNER_LIST);
                APP.eventManager.publish('CHANGE_MENU', { showDepth: 'list', props: undefined });
            }
            catch (error) {
                alert(error);
            }
            finally {
                APP.eventManager.publish('SHOW_LOADING', false);
            }
        };
        APP.eventManager.publish(popup_1.TOGGLE_POPUP, {
            component: DeletePopUpLayout_1.DeletePopUpLayout,
            componentProps: {
                deleteContent: [
                    isGlobal
                        ? '전체 배너를 삭제하시겠습니까?\n개별 설정이 없는 클래스에서는 배너가 사라집니다.'
                        : '이 클래스의 개별 배너를 삭제하시겠습니까?\n삭제 후에는 전체 배너가 노출됩니다.',
                ],
                onDelete,
            },
            width: 639,
            title: '배너 삭제',
            isFit: true,
        });
    };
    const submitLabel = isNew ? (loadedStatus === 'inherit' ? '개별 등록' : '등록') : '수정';
    const currentScopeText = isGlobal
        ? `파트너 ${effectiveTenantId}의 전체 배너 — 저장하면 이 배너가 교체됩니다`
        : loadedStatus === 'own'
            ? '이 클래스의 개별 배너 — 저장하면 교체됩니다'
            : '전체 배너(이 클래스는 개별 설정 없음) — 저장하면 이 클래스만 개별 배너로 바뀝니다';
    return (react_1.default.createElement("div", { className: "regist_container banner-item" },
        isNew && current && (loadedStatus === 'own' || loadedStatus === 'inherit') && (react_1.default.createElement("section", { className: "banner-current" },
            react_1.default.createElement("div", { className: "banner-current-head" },
                react_1.default.createElement("p", { className: "banner-current-title" },
                    "\uD604\uC7AC \uC801\uC6A9 \uC911\uC778 \uBC30\uB108",
                    react_1.default.createElement("span", { className: "banner-hint" }, currentScopeText)),
                react_1.default.createElement(common_1.Button, { className: "btn-semi-round btn-blue btn-xmd", text: "\uC774 \uB0B4\uC6A9 \uBD88\uB7EC\uC640\uC11C \uC218\uC815", onClick: loadCurrentIntoForm })),
            react_1.default.createElement("dl", { className: "banner-fields" },
                react_1.default.createElement("dt", null, "\uC81C\uBAA9"),
                react_1.default.createElement("dd", { title: current.title }, current.title),
                react_1.default.createElement("dt", null, "\uC124\uBA85"),
                react_1.default.createElement("dd", { title: current.description }, current.description || '-'),
                react_1.default.createElement("dt", null, "\uBC84\uD2BC \uBB38\uAD6C"),
                react_1.default.createElement("dd", null, current.button_text || '-'),
                react_1.default.createElement("dt", null, "\uBC84\uD2BC \uB9C1\uD06C"),
                react_1.default.createElement("dd", { title: current.button_link }, current.button_link || '-')),
            react_1.default.createElement(BannerPreview_1.BannerPreview, { banner: current, mode: "pc" }))),
        react_1.default.createElement("form", { onSubmit: e => e.preventDefault() },
            react_1.default.createElement("div", { className: "regist_box_wrapper" },
                react_1.default.createElement("div", { className: "regist_box" },
                    isTargetFixed ? (react_1.default.createElement(ui_1.TextInput, { title: "\uC801\uC6A9 \uB300\uC0C1", defaultValue: targetLabel, disabled: true, noShowButtonArea: true })) : (react_1.default.createElement(DropDownInput_1.DropDownInput, { title: "\uC801\uC6A9 \uB300\uC0C1", defaultValue: isGlobal ? GLOBAL_OPTION_ID : targetClassId, dropdownList: targetOptions, noShowButtonArea: true, handlerUpdate: value => setTargetClassId(value === GLOBAL_OPTION_ID ? banner_1.GLOBAL_BANNER_CLASS_ID : value) })),
                    isGlobal && isDefaultAdmin && (react_1.default.createElement(DropDownInput_1.DropDownInput, { title: "\uD30C\uD2B8\uB108", defaultValue: partnerId, dropdownList: tenantOptions, noShowButtonArea: true, handlerUpdate: setPartnerId })),
                    react_1.default.createElement(ui_1.TextInput, { key: `title-${formVersion}`, title: "\uC81C\uBAA9", defaultValue: initial.title, handlerUpdate: updateField('title'), placeholder: "\uC608) \uB300\uC778\uB77C \uB300.\uC138.\uD398 \uC774\uBCA4\uD2B8! (~9/27)", maxLength: TITLE_MAX, noShowButtonArea: true }),
                    react_1.default.createElement(ui_1.TextInput, { key: `description-${formVersion}`, title: "\uC124\uBA85", defaultValue: initial.description, handlerUpdate: updateField('description'), placeholder: "\uC608) \uAD50\uC7AC \uBB34\uB8CC \uBC30\uC1A1, \uC2DC\uC98C2 \uB2E4\uC2DC\uBCF4\uAE30 30\uC77C \uD2B9\uBCC4\uC5F0\uC7A5", maxLength: DESC_MAX, noShowButtonArea: true }),
                    react_1.default.createElement(ui_1.TextInput, { key: `button_text-${formVersion}`, title: "\uBC84\uD2BC \uBB38\uAD6C", defaultValue: initial.button_text, handlerUpdate: updateField('button_text'), placeholder: "\uC608) \uC2DC\uC98C3 \uC218\uAC15\uC2E0\uCCAD\uD558\uAE30 (\uBE44\uC6B0\uBA74 \uBC84\uD2BC \uC5C6\uC74C)", maxLength: CTA_MAX, noShowButtonArea: true }),
                    react_1.default.createElement(ui_1.TextInput, { key: `button_link-${formVersion}`, title: "\uBC84\uD2BC \uB9C1\uD06C", defaultValue: initial.button_link, handlerUpdate: updateField('button_link'), placeholder: "https://  (\uBE44\uC6B0\uBA74 \uD074\uB9AD\uD574\uB3C4 \uC774\uB3D9\uD558\uC9C0 \uC54A\uC74C)", noShowButtonArea: true }),
                    react_1.default.createElement("div", { className: "banner-form-hints" },
                        react_1.default.createElement("p", { className: "banner-hint scope" }, scopeText),
                        react_1.default.createElement("p", { className: `banner-hint ${loadedStatus === 'inherit' ? 'warn' : ''}` }, statusHint),
                        react_1.default.createElement("p", { className: "banner-hint" }, "\uC81C\uBAA9\u00B7\uC124\uBA85\uC740 \uD55C \uC904\uB85C \uD45C\uC2DC\uB418\uBA70 \uAE38\uBA74 \uB9D0\uC904\uC784(\u2026) \uCC98\uB9AC\uB429\uB2C8\uB2E4. \uBAA8\uBC14\uC77C \uBC84\uD2BC \uBB38\uAD6C\uB294 \uACF5\uBC31 \uAE30\uC900\uC73C\uB85C \uB450 \uC904\uB85C \uB098\uB269\uB2C8\uB2E4. \uBC30\uB108\uB294 \uC0C8 \uD0ED\uC73C\uB85C \uC5F4\uB9BD\uB2C8\uB2E4."))))),
        react_1.default.createElement(BannerPreview_1.BannerPreview, { banner: form }),
        react_1.default.createElement("div", { className: "btn-container" },
            !isNew && loadedStatus === 'own' && (react_1.default.createElement(common_1.Button, { text: "\uC0AD\uC81C", className: "btn-default-round btn-peach btn-xmd", onClick: handlerDelete })),
            react_1.default.createElement(common_1.Button, { text: submitLabel, className: "btn-default-round btn-blue btn-xmd", onClick: handlerSubmit }),
            react_1.default.createElement(common_1.Button, { text: "\uCDE8\uC18C", className: "btn-default-round btn-grey btn-xmd", onClick: cancelMenu }))));
});
